<template>
  <div>
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title class="font-serif">
        <router-link class="text-decoration--none" :to="{name: 'editors'}">Editor</router-link>
        <v-icon>{{ icons.arrow }}</v-icon>
        <span v-if="editor" class="text-capitalize">{{ editor.full_name }}</span>
      </v-toolbar-title>
      <v-spacer></v-spacer>
    </v-app-bar>
    <div class="ma-4" v-if="form">
      <div
        class="display-2 font-weight-light mb-10 font-serif text-capitalize"
        v-if="editor"
      >{{ editor.full_name }}</div>

      <avatar-form
        :loading="avatarLoading"
        :user="editor"
        @change="avatarChange"
        @avatarRemove="editorAvatarRemoved"
      />

      <v-form ref="form" v-if="form" style="max-width: 400px">
        <label class="text-field-label pl-4">Name</label>
        <v-text-field
          flat
          solo
          required
          background-color="grey lighten-3"
          class="mt-2"
          v-model="form.full_name"
          :error-messages="form.$getError('full_name')"
        ></v-text-field>

        <label class="text-field-label pl-4">Email Address</label>
        <v-text-field
          flat
          solo
          readonly
          required
          class="mt-2"
          background-color="grey lighten-3"
          v-model="form.email"
          :error-messages="form.$getError('email')"
        >
          <v-tooltip slot="append" top v-if="editor.email">
            <template v-slot:activator="{ on }">
              <v-icon color="success" v-on="on" v-if="editor.email_verified">{{ icons.check }}</v-icon>
              <v-icon color="warning" v-on="on" v-else>{{ icons.alert }}</v-icon>
            </template>
            <span v-if="editor.email_verified">Email Verified</span>
            <span v-else>Unverified email</span>
          </v-tooltip>
        </v-text-field>

        <label class="text-field-label">Status</label>
        <v-switch
          class="ml-2"
          color="red"
          @change="toggleBlockEditor"
          v-model="editor.is_blocked"
          :label="editor.is_blocked ? `Blocked` : `Block`"
          :loading="loading"
        ></v-switch>

        <v-btn
          color="primary"
          depressed
          large
          class="mt-10 mr-4"
          @click="updateEditorDetails"
          :loading="form.$busy"
        >Update</v-btn>

        <v-btn
          color="red accent-4"
          depressed
          large
          class="mt-10 mr-4 white--text"
          @click="showConfirmDialog = !showConfirmDialog"
          :loading="form.$busy"
        >Delete</v-btn>
      </v-form>
    </div>
    <v-overlay absolute :value="loading" opacity="0.2">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </v-overlay>

    <v-snackbar v-model="snackbar.show" right>
      {{ snackbar.message }}
      <v-btn color="pink" text @click="snackbar.show = false">Ok</v-btn>
    </v-snackbar>

    <confirm-dialog
      v-model="showConfirmDialog"
      :width="400"
      @deleteDetails="deleteEditorDetails"
    ></confirm-dialog>
  </div>
</template>

<script>
import { mdiChevronRight, mdiCheck, mdiAlertCircleOutline } from '@mdi/js'
import { mapState, mapActions, mapMutations } from 'vuex'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import ConfirmDialog from '@/components/ConfirmDialog'
import AvatarForm from '../Users/components/AvatarForm'

import Form from '@/utils/form'

export default {
  name: 'EditorDetails',

  components: {
    AppBarNavIcon,
    AvatarForm,
    ConfirmDialog
  },

  data () {
    return {
      form: null,
      loading: false,
      avatarLoading: false,
      showCropperModal: false,
      showConfirmDialog: false,
      avatarTimestamp: new Date().getTime(),
      icons: {
        arrow: mdiChevronRight,
        check: mdiCheck,
        alert: mdiAlertCircleOutline
      },
      snackbar: {
        show: false,
        message: null
      }
    }
  },

  created () {
    this.getEditor()
  },

  computed: {
    ...mapState({
      editor: state => state.editor.editorDetails,
      authUser: state => state.auth.user
    })
  },

  methods: {
    ...mapActions({
      changeAvatar: 'editor/changeAvatar',
      getEditorDetails: 'editor/getEditorDetails',
      updateEditor: 'editor/updateEditor',
      removeEditor: 'editor/removeEditor',
      toggleBlock: 'editor/toggleBlock'
    }),

    ...mapMutations({
      clearEditorDetails: 'editor/clearEditorDetails',
      editorAvatarRemoved: 'editor/editorAvatarRemoved'
    }),

    async getEditor () {
      this.loading = true
      await this.getEditorDetails(this.$route.params.id)
      this.form = new Form(this.editor)
      this.loading = false
    },

    async avatarChange (image) {
      this.avatarLoading = true
      let data = new FormData()

      data.append('editor_id', this.editor.id)
      data.append('avatar', image)

      await this.changeAvatar(data)
      this.avatarLoading = false
      this.showSnackbar('Avatar successfully updated!')
    },

    async updateEditorDetails () {
      this.form.$busy = true
      this.form.$clearErrors()

      this.updateEditor({
        id: this.editor.id,
        full_name: this.form.full_name
      }).then(() => {
        this.form.$busy = false
        this.showSnackbar('User details successfully updated!')
      }).catch(err => {
        this.form.$busy = false
        if (err.response.status === 422) {
          this.form.$setErrors(err.response.data.errors)
        }
      })
    },

    async toggleBlockEditor () {
      this.loading = true
      await this.toggleBlock(this.editor)

      this.is_blocked = this.editor.is_blocked
      this.loading = false

      if (await this.editor.is_blocked) {
        this.showSnackbar('Editor has been blocked!')
      } else {
        this.showSnackbar('Editor has been unblocked!')
      }
    },

    async deleteEditorDetails () {
      this.loading = true
      this.removeEditor(this.$route.params.id)
        .then(() => {
          this.$router.push({ name: 'editors' })
        }).catch(err => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
      this.loading = false
    },

    showSnackbar (message) {
      this.snackbar.message = message
      this.snackbar.show = true
    }
  },

  destroyed () {
    this.clearEditorDetails()
  },

  watch: {
    $route () {
      this.getEditor()
    }
  }
}
</script>

<style>
</style>
